import { FC, Fragment, useEffect, useState } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { v4 } from 'uuid';
// Models
import IBucket from 'models/Bucket';
// Types
import QuizTypes from 'types/QuizTypes';
import MediaFormats from 'types/MediaFormats';
import QuestionTypes from 'types/QuestionTypes';
import PageTypes from 'types/PageTypes';
import ConsentsStatuses from 'types/ConsentsStatuses';
// Store
import { useAppSelector, useAppDispatch } from 'hooks/useStore';
// Async
import ConsentsAsync from 'store/consents/consentsAsync';
// Actions
import { createQuiz } from 'store/quiz/quizzesAsync';
// Selectors
import { selectLoading } from 'store/quiz/quizzesSelectors';
import { getNamespaceItemsForSelect } from 'store/namespace/namespaceSelectors';
import { selectCurrentUser } from 'store/currentUser/currentUserSelectors';
import { selectConsents } from 'store/consents/consentsSelectors';
// MUI
import { ButtonGroup } from '@material-ui/core';
// Layouts
import { Dialog, DialogTitle, DialogContent, DialogActions } from 'layouts/Dialog';
// Dialog
import NamespaceFormDialog from 'dialogs/NamespaceForm.dialog';
// Components
import ImageUpload from 'components/ImageUpload';
import { Input, Select, Toggle } from 'components/Controls';
import { Button } from 'components/Buttons';
import DatePicker from 'components/DatePicker';
import QuizPersonalityFields from './QuizPersonalityFields';
import StyledMuiButton from 'components/StyledMuiButton';
// Utilities
import { isRequired, noWhitespace } from 'utilities/validation';
import { quizTypesLabels, teamSelectorFormations } from 'utilities/utilities';
import { buildQuizTypeDefaults } from 'utilities/quizTypeDefaults';
// Initial form
import { initialQuizForm } from 'store/form/formInitial';

type Props = {
  open: boolean;
  onClose: () => void;
};

interface IFormData {
  type: QuizTypes;
  teamSelectorFormation: string;
  namespace: string;
  quizName: string;

  // Quiz:Scroring
  startsDate: any;
  endsDate: string;
  publicLeaderboard: {
    enabled: boolean;
  };
  // Quiz:Personality
  buckets: IBucket[];

  logoImage: string;
  logoFormat: MediaFormats;
}

const QuizFormDialog:FC<Props> = ({
  // Props
  open, onClose
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  
  const namespaceOptions = useAppSelector(getNamespaceItemsForSelect);
  const loading:boolean = useAppSelector(selectLoading);
  const currentUser = useAppSelector(selectCurrentUser);
  const consents = useAppSelector(selectConsents);

  const quizTypes = currentUser?.account?.quizTypes || [];
  const defaultType = quizTypes.includes(QuizTypes.Scoring) ? QuizTypes.Scoring : quizTypes[0];

  const [ dialogNamespaceFormOpen, setDialogNamespaceFormOpen ] = useState(false);

  const toggleNamespaceFormDialog = () => setDialogNamespaceFormOpen(!dialogNamespaceFormOpen);

  const methods = useForm<IFormData>({
    defaultValues: {
      type: defaultType,
      teamSelectorFormation: '4-4-2',
      namespace: '',
      quizName: '',
      startsDate: new Date(),
      endsDate: '',
      publicLeaderboard: {
        enabled: true,
      },
      // Personality
      buckets: [],

      logoImage: '',
      logoFormat: MediaFormats.Logo,
    }
  });

  const { control, handleSubmit, formState:{ errors }, watch } = methods;

  const onSubmit = handleSubmit((data:IFormData) => {
    const { startsDate, endsDate, buckets, publicLeaderboard, ...otherData } = data;

    const requriedConsents = consents?.filter(consent => consent.language === 'en' && consent.required).map(consent => consent._id) || [];

    const typeDefaults = buildQuizTypeDefaults(otherData.type);

    const nextData:any = {
      ...otherData,
      consents: requriedConsents,
      theme: {
        ...initialQuizForm.theme
      },
      landingPage: {
        ...initialQuizForm.landingPage,
        ...typeDefaults.landingPage,
        backgroundImage: 'https://media.quizwizards.com/bg-home-2.jpg',
      },
      questions: [],
      pages: [],
      registrationPage: {
        anonymous: false,
        showPriorQuiz: false,
        showDateOfBirth: true,
        showAgreeAllCheckbox: true,
        showRequiredHint: true,
        ...typeDefaults.registrationPage,
      },
      questionsSettings: {
        ...initialQuizForm.questionsSettings,
        limit: undefined
      },
      countdownPage: {
        ...initialQuizForm.countdownPage,
        ...typeDefaults.countdownPage,
        title: 'Get Ready to Play!',
        text2: '',
      },
      resultsPage: {
        ...initialQuizForm.resultsPage,
        ...typeDefaults.resultsPage,
      },
      scoringResultPage: {
        ...initialQuizForm.scoringResultPage,
        bands: initialQuizForm.scoringResultPage.bands.map((band:any) => ({ ...band, ref: v4() })),
      },
      backgroundImage: 'https://media.quizwizards.com/bg-main-2.jpg',
      wallpaperImage: 'https://media.quizwizards.com/wallpaper-default.jpg',
    }
    if ( startsDate ) nextData['startsDate'] = moment(startsDate).format('x');
    if ( endsDate ) nextData['endsDate'] = moment(endsDate).format('x');
    if ( otherData.type === QuizTypes.Personality ){
      if ( !buckets.length ) return;
      nextData['buckets'] = buckets;
    } else {
      nextData['postQuizPages'] = [{ questions: [] }];
    }
    if ( ![QuizTypes.Personality, QuizTypes.Survey].includes(otherData.type) ) {
      nextData.publicLeaderboard = {
        ...initialQuizForm.publicLeaderboard,
        ...publicLeaderboard,
        ...typeDefaults.publicLeaderboard,
      };

      nextData.callToAction = {
        enabled: true,
        clickBonusPoints: '500',
        ...typeDefaults.callToAction,
      };
    }
    if ( otherData.type === QuizTypes.TeamSelector ) {
      teamSelectorFormations[otherData.teamSelectorFormation as keyof typeof teamSelectorFormations].forEach((position) => {
        const ref = v4();
        const question:any = {
          ref,
          title: `Pick your ${position.label}`,
          description: '',
          type: QuestionTypes.SingleChoice,
          image: 'https://media.quizwizards.com/question-default.jpg',
          video: '',
          audio: '',
          correctAnswerDescription: '',
          answers: [{ title: '', ref: v4() }, { title: '', ref: v4() }],
        }
        nextData.questions.push(question);
        const uuid = v4();
        const page:any = { uuid, refs: [ref], type: PageTypes.Question };
        nextData.pages.push(page);
      })
    }
    createQuizAsync(nextData);
  });

  const createQuizAsync = async (data:IFormData) => {
    try {
      await dispatch(createQuiz(data)).unwrap();
      onClose();
    } catch(err){}
  }

  useEffect(() => {
    // eslint-disable-next-line
    dispatch(ConsentsAsync.getConsents({ status: ConsentsStatuses.published, ['include.platform']: true }));
    // eslint-disable-next-line
  }, []);

  const typeOptions = currentUser?.account?.quizTypes?.map((quizType: QuizTypes) => ({
    value: quizType,
    label: quizTypesLabels[quizType],
  })) || [];

  const watchedType = watch('type');
  const logoFormat = watch('logoFormat');

  return (
    <Fragment>
      <Dialog size="lg" open={open} onClose={onClose}>
        <FormProvider {...methods}>
          <form onSubmit={onSubmit} noValidate>
            <DialogTitle title={t('dialogs.quiz.title')} onClose={onClose} />
            <DialogContent>
              <div className="flex flex-wrap laptop:flex-nowrap gap-6">
                <div className="w-full tablet:w-full laptop:w-1/2">
                  <div className="mb-4">
                    <h2 className="flex-grow text-white text-2xl leading-6 font-bold">{t('dialogs.quiz.subtitle')}</h2>
                  </div>
                  <Controller
                    control={control} name="type" rules={{ required: isRequired }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label={t('labels.type')} id="type"
                        options={typeOptions}
                        required={true}
                        error={Boolean(errors.type)}
                        errorText={errors.type ? errors.type.message : ''}
                        helperText={t('dialogs.quiz.typeHint')}
                      />
                    )}
                  />
                  {watchedType === QuizTypes.TeamSelector ? (
                    <Controller
                      control={control} name="teamSelectorFormation" rules={{ required: isRequired }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          label={t('labels.teamFormation')} id="teamSelectorFormation"
                          options={Object.keys(teamSelectorFormations).map(formation => ({ value: formation, label: formation }))}
                          required={true}
                          error={Boolean(errors.teamSelectorFormation)}
                          errorText={errors.teamSelectorFormation ? errors.teamSelectorFormation.message : ''}
                        />
                      )}
                    />
                  ) : null}

                  {watchedType === QuizTypes.Personality ? (
                    <span className="block text-gray-400 text-sm font-light leading-5 mb-4">
                      {t('dialogs.quiz.personalityTypeDescription')}
                    </span>
                  ) : null}

                  <Controller
                    control={control} name="quizName" rules={{ required: isRequired, validate: noWhitespace }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="quizName"
                        label={watchedType === QuizTypes.Survey ? t('pages.quizForm.surveyNameLabel') : t('pages.quizForm.quizNameLabel')}
                        required={true}
                        error={Boolean(errors.quizName)}
                        errorText={errors.quizName ? errors.quizName.message : ''}
                      />
                    )}
                  />
                  <Controller
                    control={control} name="namespace" rules={{ required: isRequired }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label={t('labels.category')} id="namespace" name="namespace"
                        required={true}
                        options={[
                          { value: '', label: t('labels.chooseCategory') },
                          ...namespaceOptions
                        ]}
                        error={Boolean(errors.namespace)}
                        errorText={errors.namespace ? errors.namespace.message : ''}
                        helperText={t('pages.quizForm.categoryHint')}
                      />
                    )}
                  />
                  <div className="-mt-3 mb-4">
                    <button
                      className="uppercase text-primary font-bold hover:underline"
                      type="button"
                      onClick={toggleNamespaceFormDialog}
                    >{t('pages.quizForm.createCategoryButton')}</button>
                  </div>
                  <div className="flex flex-wrap -mx-2">
                    <div className="w-full sm:w-1/2 px-2">
                      <div className="mb-4">
                        <Controller
                          control={control} name="startsDate"
                          render={({ field }) => (
                            <DatePicker
                              {...field}
                              label={t('labels.startDate')}
                              id="startsDate"
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="w-full sm:w-1/2 px-2">
                      <div className="mb-4">
                        <Controller
                          control={control} name="endsDate"
                          render={({ field }) => (
                            <DatePicker
                              {...field}
                              label={t('labels.endDate')}
                              id="endsDate"
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  {watchedType === QuizTypes.Prediction && (
                    <span className="block text-gray-400 text-sm font-light leading-5">
                      {t('pages.quizForm.predictionDatesHint')}
                    </span>
                  )}
                  {watchedType === QuizTypes.Personality ? (
                    <QuizPersonalityFields />
                  ) : null}
                  {[QuizTypes.Scoring, QuizTypes.Prediction, QuizTypes.Poll, QuizTypes.RankingPoll, QuizTypes.TeamSelector, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(watchedType) && (
                    <div className="pt-4">
                      <div className="flex items-center mb-4">
                        <span className="block text-white text-base font-semibold mr-3">
                          {[QuizTypes.Poll, QuizTypes.RankingPoll, QuizTypes.TeamSelector].includes(watchedType) ? t('pages.quizForm.publicLeaderboard.enabledPollLabel') : t('pages.quizForm.publicLeaderboard.enabledScoringLabel')}
                        </span>
                        <Controller
                          control={control} name="publicLeaderboard.enabled"
                          render={({ field: { onChange, value } }) => (
                            <Toggle
                              id="publicLeaderboardEnabled"
                              checked={value}
                              onChange={onChange}
                            />
                          )}
                        />
                      </div>
                      <span className="block text-gray-400 text-sm">
                        {[QuizTypes.Poll, QuizTypes.RankingPoll, QuizTypes.TeamSelector].includes(watchedType) ? t('pages.quizForm.publicLeaderboard.enabledPollHint') : t('pages.quizForm.publicLeaderboard.enabledHint')}
                      </span>
                    </div>
                  )}
                </div>
                <div className="w-full tablet:w-full laptop:w-1/2">
                  <div className="mb-4">
                    <h2 className="text-white text-2xl leading-6 font-bold">{t('labels.images')}</h2>
                  </div>
                  <Controller
                    control={control} name="logoFormat"
                    render={({ field: { onChange, value } }) => (
                      <ButtonGroup size="small" style={{ marginBottom: '16px' }}>
                        <StyledMuiButton
                          variant={value === MediaFormats.Logo ? 'contained' : 'outlined'}
                          onClick={() => onChange(MediaFormats.Logo)}
                        >{t('labels.squareLogo')}</StyledMuiButton>
                        <StyledMuiButton
                          variant={value === MediaFormats.RectLogo ? 'contained' : 'outlined'}
                          onClick={() => onChange(MediaFormats.RectLogo)}
                        >{t('labels.rectangularLogo')}</StyledMuiButton>
                      </ButtonGroup>
                    )}
                  />
                  <Controller
                    control={control} name="logoImage" rules={{ required: isRequired }}
                    render={({ field }) => (
                      <ImageUpload
                        {...field}
                        key={logoFormat}
                        className="w-1/4"
                        format={logoFormat}
                        required={true}
                        label="labels.logoImage"
                        error={Boolean(errors.logoImage)}
                        errorText={errors.logoImage ? errors.logoImage.message : ''}
                      />
                    )}
                  />
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={onClose}
              >{t('buttons.cancel')}</Button>
              <Button
                className="ml-2"
                variant="contained"
                type="submit"
                loading={loading}
              >{t('buttons.create')}</Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
      {dialogNamespaceFormOpen ? (
        <NamespaceFormDialog
          open={dialogNamespaceFormOpen}
          onClose={toggleNamespaceFormDialog}
        />
      ) : null}
    </Fragment>
  )
}

export default QuizFormDialog;
